import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { OrderService } from '../../../services/order.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { MessageService } from 'primeng/api';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Paginator } from 'primeng/paginator';
import { StorageService } from 'src/app/services/storage.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { TableColumn, DataTableComponent } from 'src/app/shared-components/data-table/data-table.component';

export function noSpacesValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control.value && typeof control.value === 'string') {
      if (control.value.trim().length === 0) {
        return { noSpaces: true };
      }
    }
    return null;
  };
}

export interface BulkFileDetail {
  file_id: number;
  file_name: string;
  status: string;
  order_id: number | null;
  referance_id: number | string;
  error_log: string | null;
}

export interface BulkOrderGroup {
  bulk_order_uuid: string;
  bulk_order_id: string;
  client_id: string;
  status: string;
  created_at: string;
  reviewer_email: string | null;
  total_files: number;
  details: BulkFileDetail[];
}

@Component({
    selector: 'app-automated-bulk-order',
    templateUrl: './automated-bulk-order.component.html',
    styleUrls: ['./automated-bulk-order.component.scss'],
    standalone: false
})
export class AutomatedBulkOrderComponent implements OnInit, OnChanges {
  @Input() orderOption: any;
  @Output() automatedBulkOrderChange: EventEmitter<any> = new EventEmitter<[]>();
  @ViewChild('paginatorComponent') paginator: Paginator;
  @ViewChild('viewOrderData') viewOrderData: TemplateRef<any>;
  @ViewChild('groupTable') groupTable: DataTableComponent;


  readonly groupColumns: TableColumn[] = [
    { field: 'bulk_order_id', header: 'Order No', sortable: true },
    { field: 'bulk_order_uuid', header: 'Order UUID', sortable: true },
    { field: 'account_name', header: 'Account Name', sortable: true, sortField: 'client_id' },
    { field: 'created_by_email', header: 'Created By', sortable: true, sortField: 'client_id' },
    { field: 'total_files', header: 'Total Files', sortable: true },
    { field: 'created_at', header: 'Order Date', sortable: true, pipe: 'date:dd/MM/yyyy hh:mm a' },
    { field: 'reviewer_email', header: 'Reviewer Email', sortable: true, fallback: '—' },
    { field: 'status', header: 'Status', sortable: true },
    { field: 'action', header: 'Action', toggleable: false },
  ];

  // ── View state ─────────────────────────────────────────────
  viewLevel: 'groups' | 'files' = 'groups';
  selectedGroup: BulkOrderGroup | null = null;

  // ── Groups table ───────────────────────────────────────────
  bulkOrderGroups: BulkOrderGroup[] = [];
  pageNo: number = 1;
  totalRecords: number = 10;
  rowsPerPage: any = 10;
  searchPageNumber: number = 1;
  numberoftables: number;
  isDateRangeValid: boolean = false;
  start_date_filter: Date;
  end_date_filter: Date;
  id: any;
  currentSortColumn: string = '';
  sortDirection: string = 'asc';
  userRole: string;
  today = new Date();
  formattedDateTime: string;

  orderFilters: any = {
    action_type: 'automated_bulk',
    page: 1,
    limit: 10,
    account_id: '',
    user: '',
    status: '',
  };
  stateData: any;
  branchData: any;
  viewOrderId: any;
  isBulkCatUploadOrder: boolean = false;
  userId: any = null;

  constructor(
    private cookieService: CookieService,
    private orderService: OrderService,
    private router: Router,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private messageService: MessageService,
    private formBuilder: FormBuilder,
    private storageService: StorageService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {
    const year = this.today.getFullYear();
    const month = String(this.today.getMonth() + 1).padStart(2, '0');
    const day = String(this.today.getDate()).padStart(2, '0');
    const hours = this.today.getHours();
    const minutes = String(this.today.getMinutes()).padStart(2, '0');
    const meridiem = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
    this.formattedDateTime = `${year}-${month}-${day} ${formattedHours}:${minutes} ${meridiem}`;

    this.userRole = this.storageService.getRole() || null;
    this.orderFilters['account_id'] = this.storageService.getAccountId() || null;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orderOption']) {
      this.viewLevel = 'groups';
      this.selectedGroup = null;

      const currentValue = changes['orderOption'].currentValue;
      const orderStatus = currentValue['bulkEstampOrderStatus'];

      if (currentValue['tabIndex'] == 2) {
        this.orderFilters.status = orderStatus;
        this.orderFilters.page = 1;
        this.orderFilters.limit = 10;

        const startDate = currentValue['startDate'];
        const endDate = currentValue['endDate'];

        this.start_date_filter = startDate;
        this.end_date_filter = endDate;

        if (startDate && endDate) {
          const timeDiff = Math.abs(
            new Date(endDate).getTime() - new Date(startDate).getTime(),
          );
          this.isDateRangeValid =
            Math.ceil(timeDiff / (1000 * 60 * 60 * 24)) <= 7;
        } else {
          this.isDateRangeValid = false;
        }

        this.getBulkOrderGroups();
      }
    }
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id');
    this.route.queryParams.subscribe((queryParams) => {
      this.id = queryParams?.id;
    });

    // this.getStateDetails();
    this.getUserDetailsByEmail();
  }

  getUserDetailsByEmail(): void {
    const payload = {
      account_id: this.storageService.getAccountId(),
      user_email: this.storageService.getLoginEmail(),
    };

    this.orderService.getUserDetailsByEmail(payload).subscribe((res: any) => {
      this.userId = res?.data?.[0]?.user_id;
      this.getBulkOrderGroups();
    });
  }

  // ── API ────────────────────────────────────────────────────
  private _bulkOrderGroupsRequestId = 0;

  getBulkOrderGroups(): void {
    const payload = {
      page: this.orderFilters.page || 1,
      limit: this.orderFilters.limit || 10,
      user_id: this.userId,
      status: this.orderFilters.status ? this.orderFilters.status : '',
      account_id: this.storageService.getAccountId()
    };

    const requestId = ++this._bulkOrderGroupsRequestId;

    this.orderService.getBulkCatUploadOrders(payload).subscribe(
      (resp) => {
        if (requestId !== this._bulkOrderGroupsRequestId) return;

        const isSuccess = resp.status === 'success' || resp.status == 200;
        const data = resp.data ?? resp.content ?? [];

        if (isSuccess && Array.isArray(data) && data.length > 0) {
          this.bulkOrderGroups = data;
          this.totalRecords =
            resp.pagination?.total_records ?? this.bulkOrderGroups.length;
          this.searchPageNumber = resp.pagination?.current_page ?? 1;
          this.numberoftables = Math.ceil(this.totalRecords / this.rowsPerPage);
        } else {
          this.bulkOrderGroups = [];
          this.totalRecords = 0;
          this.numberoftables = 0;
        }
        this.cdr.detectChanges();
      },
      (err) => {
        if (requestId !== this._bulkOrderGroupsRequestId) return;

        console.error('[AutomatedBulk] API error:', err);
        this.bulkOrderGroups = [];
        this.totalRecords = 0;
        this.cdr.detectChanges();
      },
    );
  }

  // ── Navigation ─────────────────────────────────────────────
  onViewFiles(group: BulkOrderGroup): void {
   const data = group as any;
    this.analyticsService.trackEvent('click_view_files_automated_bulk', {
      bulk_order_id: data.bulk_order_id,
      account_id: data.account_id,
      account_name: data.account_name
    });

    this.selectedGroup = group;
    this.viewLevel = 'files';
  }

  /** Dispatches to whichever "View" action is currently shown for this row's status. */
  viewGroup(group: BulkOrderGroup): void {
    const data = group as any;
    if (data.status === 'AWAITING_APPROVAL') {
      this.openViewandOrderModal(group);
    } else {
      this.onViewFiles(group);
    }
  }

  onBackToGroups(): void {
    this.selectedGroup = null;
    this.viewLevel = 'groups';
    this.getBulkOrderGroups();
  }

  // ── Sorting ────────────────────────────────────────────────
  sortGroups(column: keyof BulkOrderGroup): void {
    this._toggleSort(column as string);
    this.bulkOrderGroups.sort((a, b) => this._cmp(a[column], b[column]));
  }

  private _toggleSort(column: string): void {
    if (column === this.currentSortColumn) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.currentSortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  private _cmp(a: any, b: any): number {
    if (a < b) return this.sortDirection === 'asc' ? -1 : 1;
    if (a > b) return this.sortDirection === 'asc' ? 1 : -1;
    return 0;
  }

  // ── Pagination ─────────────────────────────────────────────
  onPageChange(event: any) {
    this.orderFilters.page = event.page + 1;
    this.orderFilters.limit = event.rows;
    this.rowsPerPage = event.rows;
    this.pageNo = event.page * event.rows;
    this.getBulkOrderGroups();
  }

  pageChange(page: number) {
    this.orderFilters.page = page;
    this.pageNo = (page - 1) * this.rowsPerPage;
    if (this.paginator) {
      this.paginator.changePage(page - 1);
    }
    this.getBulkOrderGroups();
  }

  onRowsPerPageChange(event: any) {
    this.rowsPerPage = event;
    this.orderFilters.limit = event;
    this.orderFilters.page = 1;
    this.pageNo = 0;
  }

  // ── Helpers ────────────────────────────────────────────────
  getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      PENDING: 'Pending',
      AWAITING_APPROVAL: 'Awaiting Approval',
      COMPLETED: 'Completed',
      FAILED: 'Failed',
      IN_PROGRESS: 'In Progress',
      CANCELLED: 'Cancelled',
    };
    return map[status] ?? status;
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      PENDING: 'otherStatusColor',
      AWAITING_APPROVAL: 'otherStatusColor',
      COMPLETED: 'compltdStatusColor',
      FAILED: 'errorStatusColor',
      IN_PROGRESS: 'otherStatusColor',
      CANCELLED: 'otherStatusColor',
    };
    return map[status] ?? 'otherStatusColor';
  }

  sendtoOrderListing() {
    this.automatedBulkOrderChange.emit('AutomatedBulk');
  }

  back() {
    this.userRole === 'CLIENT'
      ? this.router.navigate(['doqfy/dashboard'])
      : this.router.navigate(['doqfy/admin-dashboard']);
  }

  getStateDetails() {
    this.orderService.getStates().subscribe((res: any) => {
      this.stateData = res['content'];
    });
  }

  getBranchDetails(id: number) {
    this.orderService.getBranchListbyStateId(id).subscribe((res: any) => {
      this.branchData = res['content'];
      this.branchData = this.branchData.filter((e: any) => e.is_active === true);
    });
  }

  numberOnly(event: any) {
    const pattern = /[0-9\-]/;
    const inputChar = String.fromCharCode(event.charCode);
    if (
      (event.keyCode != 8 && !pattern.test(inputChar)) ||
      event.keyCode == 45
    ) {
      event.preventDefault();
    } else if (event.target.value === '0') {
      event.preventDefault();
    }
  }
  openViewandOrderModal(file: any): void {
    this.isBulkCatUploadOrder = true;
    this.viewOrderId = file?.bulk_order_uuid ?? file;
    this.cdr.detectChanges();

    this.modalService
      .open(this.viewOrderData, {
        size: 'xl',
        ariaLabelledBy: 'modal-basic-title',
        backdrop: 'static',
        keyboard: false,
      })
      .result.finally(() => {
        if (this.viewLevel === 'files' && this.selectedGroup) {
          // Refresh the group once after modal closes, then resume polling
          // if there are still non-terminal files.
          this.orderService
            .getBulkCatUploadOrders({
              bulk_order_uuid: this.selectedGroup.bulk_order_uuid,
            })
            .subscribe((resp) => {
              const data = resp.data ?? resp.content ?? [];
              if (Array.isArray(data) && data.length > 0) {
                const updated = data.find(
                  (g: BulkOrderGroup) =>
                    g.bulk_order_uuid === this.selectedGroup!.bulk_order_uuid,
                );
                if (updated) {
                  this.selectedGroup = { ...updated };
                }
              }
            });
        } else {
          this.getBulkOrderGroups();
        }
      });
  }
  closeView() {
    this.modalService.dismissAll();
    this.getBulkOrderGroups();
  }
}